import type { Deal, DealListItem, ManagerDealListItem } from "@/api/client";

import { buildManagerFeed, type ManagerFeedItemKind } from "@/components/deals/manager/sla";
import { isMeetingRewardReminderDue } from "@/components/deals/meetingRewardReminder";
import {
  buildPoOrgActivity,
  type PoOrgActivityItem,
} from "@/components/po-dashboard/poOrgActivity";

/** Cap on rendered cards — the player-coach feed is a glanceable task list, not
 * a queue. review-r2 (7715bc29): "No more than 4 feed cards showing on this
 * dashboard - the rest will be hidden." The Tier-3 training card now leads the
 * feed naturally (CHP-1004), with PoTodoFeed's pin-above-cap kept as a
 * fallback, so a compulsory-training nudge is never clipped. */
export const PO_FEED_MAX_ITEMS = 4;

/** Window (days) for the Tier-1 team-expiring source (B1). A direct-report's
 * LIVE deal closing within this many days surfaces as a coach-hat blocker.
 * Matches the Deal Explorer / Insights 30-day expiring convention so the
 * surfaces agree. */
export const PO_TEAM_EXPIRING_WINDOW_DAYS = 30;

/** Origin tag rendered on each card so a Program Owner can tell at a glance
 * whether an item belongs to the team (the coach hat) or to their own pipeline
 * (the player hat). */
export type PoFeedOrigin = "team" | "personal";

/** The weighting tiers (lower weight sorts first — EXCEPT the Tier-3
 * training card, which renders FIRST per the CHP-1004 ruling, mirroring the
 * BDM feed's mandatory-training-first ordering):
 *   - Tier 1 — TEAM SUPPORT blockers (a team member's deal needs a nudge):
 *     deep-links to the deal record.
 *   - Tier 2 — PERSONAL ACTION (the PO's own drafts / expiring deals):
 *     reuses the existing personal-feed deep-links.
 *   - Tier 3 — enablement (training): deep-links to the training surface.
 *     Hoisted to the head of the feed; hides at zero outstanding.
 *   - Tier 4 — ORG ACTIVITY notifications (CHP-267): informational awareness of
 *     other members' restricted org events; lowest weight. */
export type PoFeedTier = 1 | 2 | 3 | 4;

/** Manager-feed kinds the PO Tier-1 source deliberately does NOT surface:
 * `closed_lost` (an info card, not an actionable blocker) and `expiry_notice`
 * (main's CHP-394 manager nudge — the PO's own `team-expiring` source already
 * covers live-expiring deals with coach-appropriate copy, so surfacing both
 * would double-count). Excluding them keeps main's later manager-feed additions
 * from leaking into the player-coach feed (post-merge integration call). */
export type PoReviewKind = Exclude<ManagerFeedItemKind, "closed_lost" | "expiry_notice">;

/** The Tier-1 team-blocker classification. The four review-SLA kinds come
 * straight from the manager feed (so copy + countdown logic is shared);
 * `team-expiring` is the B1 org-scoped expiring source (a direct report's LIVE
 * deal nearing its close date); `meeting-reward-reminder` is the CHP-387 pooled-
 * payout 45-day-midpoint nudge (any org deal with no claimed first-meeting
 * reward). */
export type PoTeamFeedKind = PoReviewKind | "team-expiring" | "meeting-reward-reminder";

/** A Tier-1 team-blocker card. The CTA always deep-links to the deal record. */
export interface PoTeamFeedItem {
  tier: 1;
  origin: "team";
  /** Stable key + deep-link target. */
  id: string;
  kind: PoTeamFeedKind;
  deal: ManagerDealListItem;
}

/** A Tier-2 personal-pipeline card (the PO is the deal owner). */
export interface PoPersonalFeedItem {
  tier: 2;
  origin: "personal";
  id: string;
  kind: "expiring" | "draft" | "outcome";
  /** Expiring + draft carry a full Deal; outcome carries a DealListItem. */
  deal: Deal | DealListItem;
}

/** A Tier-3 enablement card (currently outstanding compulsory training). */
export interface PoEnablementFeedItem {
  tier: 3;
  origin: "personal";
  id: string;
  kind: "training";
  /** Count of outstanding compulsory modules driving the card copy. */
  outstandingCount: number;
}

/** A Tier-4 org-activity notification card (CHP-267) — informational awareness
 * of a restricted set of org events by another BDM/PO, attributed to the deal
 * owner's full name. Lowest weight: never crowds out an actionable item. */
export interface PoOrgActivityFeedItem {
  tier: 4;
  origin: "team";
  id: string;
  kind: "org-activity";
  activity: PoOrgActivityItem;
}

export type PoFeedItem =
  PoTeamFeedItem | PoPersonalFeedItem | PoEnablementFeedItem | PoOrgActivityFeedItem;

export interface BuildPoFeedInput {
  /** Org-wide deal ledger (getTeamDeals) — source of Tier-1 team blockers. */
  teamDeals: ManagerDealListItem[];
  /** The PO's OWN deal ids (from listMyDeals). Excluded from Tier-1 "team"
   * cards: the org-wide ledger includes the PO themselves, but a coach's own
   * deal is never a "team" blocker — it belongs in Tier-2 personal (dismissible).
   * Keyed off ids (not acknowledge state) so a PO's own deal can't re-enter
   * Tier-1 once its personal expiry card is dismissed (walk-caught). Optional
   * (defaults to none) so callers/tests without own-deal context still work. */
  ownDealIds?: string[];
  /** The PO's own active drafts (already filtered to draft + not-dismissed). */
  drafts: Deal[];
  /** The PO's own expiring live deals. */
  expiring: Deal[];
  /** The PO's own recently-decided deals (deriveOutcomeCards output). */
  outcomes: DealListItem[];
  /** Outstanding compulsory training module count for the PO. */
  outstandingTraining: number;
  /** CHP-387: whether the viewer's firm runs a POOLED incentive model. Only a
   * pooled PO gets the 45-day-midpoint "First meeting reward status" nudge for
   * org deals (a direct-payout firm shows the reminder to the BDM instead, on
   * their own dashboard). Defaults to false (no card) so callers/tests without
   * payout context still work. */
  viewerPayoutPooled?: boolean;
  /** Reference clock for the team-feed SLA window; pulled out for tests. */
  now?: Date;
}

/**
 * Build the Program Owner's weighted "player-coach" to-do feed.
 *
 * Four tiers, weight-sorted (Tier 1 → 4), capped at PO_FEED_MAX_ITEMS:
 *
 *   1. TEAM SUPPORT — actionable team blockers. First the review-SLA items
 *      classified via the manager feed's `buildManagerFeed` (registration
 *      nearing SLA, extension/win review, breach), then the B1 org-scoped
 *      `team-expiring` source (a direct report's LIVE deal nearing its close
 *      date, soonest-close then value-DESC), then — for a POOLED-payout firm
 *      only — the CHP-387 `meeting-reward-reminder` source (any org deal past
 *      its 45-day midpoint with no claimed first-meeting reward). All deep-link
 *      to the deal record.
 *   2. PERSONAL ACTION — the PO's own expiring deals, denied outcomes, and
 *      resumable drafts (the same cards the BDM personal feed shows).
 *   3. Enablement — outstanding compulsory training, when any.
 *   4. ORG ACTIVITY (CHP-267) — informational awareness of a restricted set of
 *      other org members' events, attributed to the deal owner. Lowest weight.
 *
 * Within Tier 1, breaches pin first then soonest-deadline (mirrors
 * ManagerFeedSidebar). The function never throws on empty inputs — it returns
 * `[]`, and the caller renders a positive empty state.
 */
export function buildPoFeed({
  teamDeals,
  ownDealIds = [],
  drafts,
  expiring,
  outcomes,
  outstandingTraining,
  viewerPayoutPooled = false,
  now = new Date(),
}: BuildPoFeedInput): PoFeedItem[] {
  // The PO's own deals are in the org-wide ledger but are never "team" blockers
  // (they're the coach's own pipeline → Tier-2). Exclude them from BOTH Tier-1
  // sources up front so an own deal can't surface as a team card.
  const ownIds = new Set(ownDealIds);
  const othersDeals = teamDeals.filter((d) => !ownIds.has(d.id));

  // Tier 1a — review-SLA team blockers, classified + ordered exactly like the
  // manager feed (breach pinned, then soonest-deadline first; the rare
  // deadline-less card — a pre-CHP-1176 win submission — sorts last).
  // Drop main's manager-only feed kinds: `closed_lost` (info, not actionable) and
  // `expiry_notice` (the CHP-394 snooze nudge — live-expiring deals come from the
  // dedicated `team-expiring` source below with coach copy, so keeping
  // expiry_notice here would double-count them and steal the team-expiring card).
  const teamFeed = buildManagerFeed(othersDeals, now).filter(
    (item) => item.kind !== "closed_lost" && item.kind !== "expiry_notice",
  );
  const teamPriority = (kind: ManagerFeedItemKind) => (kind === "breach" ? 0 : 1);
  const sortedReview = [...teamFeed].sort((a, b) => {
    if (teamPriority(a.kind) !== teamPriority(b.kind)) {
      return teamPriority(a.kind) - teamPriority(b.kind);
    }
    const aMs = a.deal.effective_sla_deadline
      ? Date.parse(a.deal.effective_sla_deadline)
      : Infinity;
    const bMs = b.deal.effective_sla_deadline
      ? Date.parse(b.deal.effective_sla_deadline)
      : Infinity;
    return aMs - bMs;
  });
  const tier1Review: PoTeamFeedItem[] = sortedReview.map(({ deal, kind }) => ({
    tier: 1,
    origin: "team",
    id: deal.id,
    // Safe narrowing: closed_lost/expiry_notice were filtered out of teamFeed above.
    kind: kind as PoReviewKind,
    deal,
  }));

  // Tier 1b — B1 org-scoped expiring source: a direct report's LIVE deal closing
  // within the feed window. Sorted by SOONEST close, then total_contract_value
  // DESC (bigger deals break ties). De-duplicated against the review items AND
  // the PO's own personal-expiring deals (which already surface as Tier-2) so no
  // deal appears twice. These sit AFTER the SLA-bound review items (which carry
  // hard auto-approval deadlines) and need no dismiss — acknowledgeDealExpiry is
  // owner-scoped.
  const seenIds = new Set<string>([...tier1Review.map((i) => i.id), ...expiring.map((d) => d.id)]);
  const windowMs = now.getTime() + PO_TEAM_EXPIRING_WINDOW_DAYS * 24 * 60 * 60 * 1000;
  const expiringTeam = othersDeals.filter((d) => {
    if (seenIds.has(d.id)) return false;
    if (d.deal_status !== "live" || !d.target_close_date) return false;
    const close = Date.parse(d.target_close_date);
    if (Number.isNaN(close)) return false;
    return close <= windowMs;
  });
  const sortedExpiringTeam = [...expiringTeam].sort((a, b) => {
    const aMs = a.target_close_date ? Date.parse(a.target_close_date) : Infinity;
    const bMs = b.target_close_date ? Date.parse(b.target_close_date) : Infinity;
    if (aMs !== bMs) return aMs - bMs; // soonest close first
    return (b.total_contract_value ?? 0) - (a.total_contract_value ?? 0); // then TCV DESC
  });
  const tier1Expiring: PoTeamFeedItem[] = sortedExpiringTeam.map((deal) => ({
    tier: 1,
    origin: "team",
    id: deal.id,
    kind: "team-expiring",
    deal,
  }));

  // Tier 1c — CHP-387 pooled-payout first-meeting-reward midpoint reminder. ONLY
  // a pooled-incentive firm sees this (a direct-payout firm shows the reminder
  // to the BDM on their own dashboard instead). Any org deal past its 45-day
  // midpoint with no claimed first-meeting reward (see isMeetingRewardReminderDue)
  // qualifies. De-duplicated against the review + team-expiring items already
  // present so a deal that is BOTH expiring and unclaimed surfaces once (the
  // higher-priority team-expiring/review card wins). Sorted by go-live time so
  // the deal that has been waiting longest leads.
  const tier1ReminderSeen = new Set<string>([
    ...tier1Review.map((i) => i.id),
    ...tier1Expiring.map((i) => i.id),
  ]);
  const reminderDeals = viewerPayoutPooled
    ? othersDeals.filter((d) => !tier1ReminderSeen.has(d.id) && isMeetingRewardReminderDue(d, now))
    : [];
  const sortedReminder = [...reminderDeals].sort((a, b) => {
    const aMs = a.live_since ? Date.parse(a.live_since) : Infinity;
    const bMs = b.live_since ? Date.parse(b.live_since) : Infinity;
    return aMs - bMs; // longest-live first (earliest go-live)
  });
  const tier1Reminder: PoTeamFeedItem[] = sortedReminder.map((deal) => ({
    tier: 1,
    origin: "team",
    id: deal.id,
    kind: "meeting-reward-reminder",
    deal,
  }));

  const tier1: PoTeamFeedItem[] = [...tier1Review, ...tier1Expiring, ...tier1Reminder];

  // Tier 2 — personal pipeline. Soonest-expiring first; denied outcomes are
  // actionable (re-submit / dispute) so they outrank drafts; approved outcomes
  // are good-news info and sort last in-tier.
  const sortedExpiring = [...expiring].sort((a, b) =>
    (a.target_close_date ?? "9999-12-31").localeCompare(b.target_close_date ?? "9999-12-31"),
  );
  const deniedOutcomes = outcomes.filter((d) => d.deal_status === "denied");
  const approvedOutcomes = outcomes.filter((d) => d.deal_status !== "denied");
  const tier2: PoPersonalFeedItem[] = [
    ...sortedExpiring.map((deal): PoPersonalFeedItem => ({
      tier: 2,
      origin: "personal",
      id: deal.id,
      kind: "expiring",
      deal,
    })),
    ...deniedOutcomes.map((deal): PoPersonalFeedItem => ({
      tier: 2,
      origin: "personal",
      id: deal.id,
      kind: "outcome",
      deal,
    })),
    ...drafts.map((deal): PoPersonalFeedItem => ({
      tier: 2,
      origin: "personal",
      id: deal.id,
      kind: "draft",
      deal,
    })),
    ...approvedOutcomes.map((deal): PoPersonalFeedItem => ({
      tier: 2,
      origin: "personal",
      id: deal.id,
      kind: "outcome",
      deal,
    })),
  ];

  // Tier 3 — enablement (single training card when something is outstanding).
  const tier3: PoEnablementFeedItem[] =
    outstandingTraining > 0
      ? [
          {
            tier: 3,
            origin: "personal",
            id: "training-enablement",
            kind: "training",
            outstandingCount: outstandingTraining,
          },
        ]
      : [];

  // Tier 4 — CHP-267 org-activity notifications: the restricted event set for
  // OTHER members' deals, attributed to the deal owner, newest first. Lowest
  // weight — informational awareness that never crowds out an actionable card.
  const tier4: PoOrgActivityFeedItem[] = buildPoOrgActivity(teamDeals, ownIds, now).map(
    (activity): PoOrgActivityFeedItem => ({
      tier: 4,
      origin: "team",
      id: activity.id,
      kind: "org-activity",
      activity,
    }),
  );

  // CHP-1004 ruling: the mandatory-training card renders literally FIRST
  // (mirrors the BDM DealFeedSidebar, incl. hide-at-zero via the empty tier3).
  // The tier value stays 3 — it's the card's identity for the dismiss key and
  // the PoTodoFeed pin, not its render position.
  return [...tier3, ...tier1, ...tier2, ...tier4];
}
